import { AdminSideBar, Tooltip, type IconName } from "@uigovpe/components";
import { useLocation, useNavigate } from "react-router-dom";
import brasao from "@/assets/logos/brasao.svg";
import logoEmpreendeMulher from "@/assets/logos/logo-empreende-mulher-pe.svg";
import { Routes } from "@/constants/routes";
import { useEmpreendedoraStore } from "@/stores/empreendedoraStore";
import { useEffect, useMemo, useRef } from "react";

const baseSections = [
  { id: "inicio", title: "Início", icon: "home" as IconName },
  {
    id: "meus-produtos",
    title: "Meus Produtos",
    icon: "add_business" as IconName,
  },
  {
    id: "adicionar-produto",
    title: "Adicionar Produto",
    icon: "add_business" as IconName,
  },
  {
    id: "notificacoes",
    title: "Notificações",
    icon: "notifications" as IconName,
  },
];

const sectionRoutes: Record<string, string> = {
  inicio: Routes.HOME,
  "meu-perfil": Routes.PROFILE,
  "dados-pessoais": `${Routes.PROFILE}?secao=dados-pessoais`,
  "dados-negocio": `${Routes.PROFILE}?secao=dados-negocio`,
  "endereco-atendimento": `${Routes.PROFILE}?secao=endereco-atendimento`,
  "contatos-redes-sociais": `${Routes.PROFILE}?secao=contatos-redes-sociais`,
  "meus-produtos": Routes.MY_PRODUCTS,
  "adicionar-produto": Routes.ADD_PRODUCTS,
};

const PUBLIC_SHOWCASE_URL =
  "https://empreende-mulher-vitrine.netlify.app/negocios/perfil";

const logo = {
  src: logoEmpreendeMulher,
  alt: "Empreende Mulher",
  width: 200,
  height: 40,
};
const footerSidebarLogo = {
  src: brasao,
  alt: "Empreende Mulher PE",
  width: 80,
  height: 80,
};

export default function CustomAdminSideBar() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const perfilCompleto = useEmpreendedoraStore((state) => state.perfilCompleto);
  const negocioId = useEmpreendedoraStore((state) => state.negocioId);
  const sections = useMemo(
    () => [
      baseSections[0],
      {
        id: "meu-perfil",
        title: "Meu Perfil",
        icon: "person" as IconName,
        ...(perfilCompleto
          ? {
              items: [
                { id: "dados-pessoais", label: "Dados pessoais" },
                { id: "dados-negocio", label: "Dados do negócio" },
                { id: "endereco-atendimento", label: "Endereço e atendimento" },
                {
                  id: "contatos-redes-sociais",
                  label: "Contatos e redes sociais",
                },
                {
                  id: "visualizar-perfil-publico",
                  label: "Visualizar perfil público",
                },
              ],
            }
          : {}),
      },
      ...baseSections.slice(1),
    ],
    [perfilCompleto],
  );

  useEffect(() => {
    const productSectionIds = ["meus-produtos", "adicionar-produto"];

    productSectionIds.forEach((id) => {
      const button = sidebarRef.current?.querySelector<HTMLButtonElement>(
        `#${id}`,
      );

      if (button) {
        button.disabled = !perfilCompleto;
        button.setAttribute("aria-disabled", String(!perfilCompleto));
      }
    });
  }, [perfilCompleto]);

  const handleNavigation = (event: React.MouseEvent<HTMLDivElement>) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "button[id]",
    );
    const route = button ? sectionRoutes[button.id] : undefined;

    if (button?.id === "meu-perfil" && perfilCompleto) return;

    if (button?.id === "visualizar-perfil-publico") {
      event.preventDefault();
      event.stopPropagation();
      if (negocioId) {
        window.open(
          `${PUBLIC_SHOWCASE_URL}/${encodeURIComponent(negocioId)}`,
          "_blank",
          "noopener,noreferrer",
        );
      }
      return;
    }

    if (!route) return;

    event.preventDefault();
    event.stopPropagation();
    navigate(route);
  };
  const activeProfileItem = new URLSearchParams(search).get("secao");
  const activeSectionClass =
    pathname.startsWith("/profile") || pathname.startsWith("/feedback")
      ? perfilCompleto
        ? `empreende-sidebar-active-${activeProfileItem ?? "dados-pessoais"}`
        : "empreende-sidebar-active-meu-perfil"
      : pathname.startsWith("/my-products")
        ? perfilCompleto
          ? "empreende-sidebar-active-meus-produtos"
          : ""
        : pathname.startsWith("/add-products")
          ? perfilCompleto
            ? "empreende-sidebar-active-adicionar-produto"
            : ""
          : pathname === "/" || pathname.startsWith("/dashboard")
            ? "empreende-sidebar-active-inicio"
            : "";

  return (
    <div
      ref={sidebarRef}
      className="empreende-admin-sidebar h-full bg-[#f8fafc]"
      onClickCapture={handleNavigation}
    >
      <AdminSideBar
        sections={sections}
        className={activeSectionClass}
        ui={{
          toggleButton: {
            className:
              "text-primary! hover:bg-[#f0edff]! focus-visible:bg-[#f0edff]! [&_.ui-icon]:text-primary!",
          },
        }}
        version="1.0.0"
        theme="default"
        logo={logo}
        footerSidebarLogo={footerSidebarLogo}
      />
      {!perfilCompleto && (
        <>
          <Tooltip
            target=".admin-sidebar-section:has(> #meus-produtos)"
            theme="light"
            position="mouse"
            mouseTrack
            mouseTrackTop={12}
            mouseTrackLeft={12}
            content={
              <span>
                Complete seu perfil primeiro para acessar seus produtos.
              </span>
            }
          />
          <Tooltip
            target=".admin-sidebar-section:has(> #adicionar-produto)"
            theme="light"
            position="mouse"
            mouseTrack
            mouseTrackTop={12}
            mouseTrackLeft={12}
            content={
              <span>Complete seu perfil primeiro para adicionar produtos.</span>
            }
          />
        </>
      )}
    </div>
  );
}
